import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import FileUploader from "@/components/FileUploader";
import { toast } from "sonner";

const STATUS_COPY = {
  pending_confirmation: { label: "Awaiting planner confirmation", tone: "bg-[#FEF3C7] text-[#92400E]" },
  eligible:             { label: "Eligible for payout",           tone: "bg-[#E6F6EA] text-[#1A6E3A]" },
  held:                 { label: "Held — under NContro review",   tone: "bg-[#FDECE6] text-[#B03A00]" },
  released:             { label: "Payout released",               tone: "bg-[#EFEADF] text-[#5C5C5C]" },
};

export default function ServiceStatusPanel({ bookingId, user, booking }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [evidence, setEvidence] = useState("");

  const isAdmin = user?.role === "admin";
  const isPlanner = user?.user_id === booking?.planner_id;
  const isVendor = user?.user_id === booking?.vendor_owner_id;

  const load = () => api.get(`/bookings/${bookingId}/service-status`)
    .then(({ data }) => setStatus(data))
    .catch(() => setStatus(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  if (!status || !status.event_date_passed) return null;

  const call = async (path, body) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success("Recorded");
      setNote(""); setEvidence("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const vendorMark = () => call(`/bookings/${bookingId}/service-completion`,
                                { note, evidence_ref: evidence || null });
  const plannerConfirm = () => call(`/bookings/${bookingId}/service-confirmation`,
                                    { action: "confirm", note });
  const plannerReport = (action) => {
    if (!note.trim()) { toast.error("A written explanation is required."); return; }
    call(`/bookings/${bookingId}/service-confirmation`,
         { action, note, evidence_ref: evidence || null });
  };
  const addEvidence = () => call(`/bookings/${bookingId}/service-evidence`,
                                 { note, evidence_ref: evidence || null, kind: evidence ? "photo" : "note" });
  const adminOverride = (action) => {
    if (!window.confirm(`Set payout to ${action}? This is state-only — no money moves.`)) return;
    call(`/admin/bookings/${bookingId}/payout-override?action=${action}`, {});
  };

  const s = STATUS_COPY[status.payout_status] || {};
  return (
    <section data-testid="service-status-panel" className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8 mt-8">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Service Status</div>
          <h3 className="font-serif text-xl">
            {status.service_review_required
              ? "Under NContro Review"
              : status.planner_confirmation?.action === "confirm"
              ? "Service confirmed"
              : status.vendor_completion_claim
              ? "Awaiting planner confirmation"
              : "Ready for vendor to mark completed"}
          </h3>
        </div>
        {status.payout_status && (
          <span data-testid="payout-status-pill" className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${s.tone}`}>
            {s.label || status.payout_status}
          </span>
        )}
      </div>

      {/* Vendor claim (visible to all participants) */}
      {status.vendor_completion_claim && (
        <div className="text-sm mb-3 p-3 bg-[#F5F2EB] rounded-lg" data-testid="vendor-claim-block">
          <div className="eyebrow mb-1">Vendor marked completed</div>
          <div>{status.vendor_completion_claim.note || <em className="text-[color:var(--text-muted)]">no note</em>}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">{status.vendor_completion_claim.claimed_at?.slice(0, 10)}</div>
        </div>
      )}

      {/* Planner response */}
      {status.planner_confirmation && (
        <div className="text-sm mb-3 p-3 bg-[#F5F2EB] rounded-lg" data-testid="planner-response-block">
          <div className="eyebrow mb-1">Planner response — {status.planner_confirmation.action.replace(/_/g, " ")}</div>
          <div>{status.planner_confirmation.note || <em className="text-[color:var(--text-muted)]">no note</em>}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">{status.planner_confirmation.responded_at?.slice(0, 10)}</div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3 mt-4">
        {(isVendor && !status.vendor_completion_claim) && (
          <>
            <Textarea data-testid="vendor-note" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} rows={2} />
            <FileUploader testId="vendor-evidence-upload" onUploaded={setEvidence} />
            <Button data-testid="mark-service-completed" onClick={vendorMark} disabled={busy}>Mark Service Completed</Button>
          </>
        )}
        {(isPlanner && !status.planner_confirmation && status.vendor_completion_claim) && (
          <>
            <Textarea data-testid="planner-note" placeholder="Explanation (required for reports)" value={note} onChange={e => setNote(e.target.value)} rows={2} />
            <FileUploader testId="planner-evidence-upload" onUploaded={setEvidence} />
            <div className="flex flex-wrap gap-2">
              <Button data-testid="confirm-service" onClick={plannerConfirm} disabled={busy}>Confirm Completed</Button>
              <Button data-testid="report-no-show" onClick={() => plannerReport("report_no_show")} disabled={busy} variant="outline">Vendor Didn't Show</Button>
              <Button data-testid="report-problem" onClick={() => plannerReport("report_problem")} disabled={busy} variant="outline">Report a Problem</Button>
            </div>
          </>
        )}

        {/* Anyone (participant) can add additional evidence */}
        {(isVendor || isPlanner || isAdmin) && (status.vendor_completion_claim || status.planner_confirmation) && (
          <div className="pt-3 border-t border-[color:var(--border)]">
            <div className="eyebrow mb-2">Add additional evidence</div>
            <Textarea data-testid="evidence-note" placeholder="Note" value={note} onChange={e => setNote(e.target.value)} rows={2} />
            <div className="mt-2"><FileUploader testId="evidence-upload" onUploaded={setEvidence} /></div>
            <Button data-testid="add-evidence" onClick={addEvidence} disabled={busy || (!note && !evidence)} variant="outline" className="mt-2">Attach</Button>
          </div>
        )}

        {isAdmin && status.service_review_required && (
          <div className="pt-3 border-t border-[color:var(--border)] flex flex-wrap gap-2">
            <div className="eyebrow w-full mb-1">Admin override</div>
            <Button data-testid="admin-override-eligible" onClick={() => adminOverride("eligible")} disabled={busy} variant="outline">Mark eligible for payout</Button>
            <Button data-testid="admin-override-held" onClick={() => adminOverride("held")} disabled={busy} variant="outline">Keep held</Button>
          </div>
        )}
      </div>

      {/* Timeline */}
      {status.events?.length > 0 && (
        <details className="mt-6" data-testid="service-events-details">
          <summary className="text-sm text-[color:var(--text-muted)] cursor-pointer">View timeline ({status.events.length})</summary>
          <ul className="mt-3 space-y-1 text-xs text-[color:var(--text-muted)]">
            {status.events.map(ev => (
              <li key={ev.event_id}><span className="uppercase tracking-widest">{ev.event_type.replace(/_/g, " ")}</span> · {ev.created_at?.slice(0, 10)} {ev.payload?.note ? `— ${ev.payload.note.slice(0, 80)}` : ""}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
